import { TabContext, TabPanel } from "@mui/lab";
import { Drawer, Tab, Tooltip } from "@mui/material";
import { DownloadOutline } from "assets/img/svg/downloadOutline";
import Plus from "assets/img/svg/plus";
import Upload from "assets/img/svg/upload";
import classNames from "classnames";
import AuthPage from "components/shared/AuthPage";
import ControlFilter from "components/shared/ControlFilter";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import ModalBase from "components/shared/ModalBase";
import SmallSearchBar from "components/shared/SmallSearchBar";
import type { IStatusRule } from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import YearDatePicker from "components/shared/YearDatePicker";
import { useAuth } from "contexts/auth/AuthContext";
import useAPIPagination from "hooks/useAPIPagination";
import { useDebounce } from "hooks/useDebounce";
import usePageTitle from "hooks/usePageTitle";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { IntegrationStatus } from "store/integrationsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { downloadTransferTermSales, ETransferTermSalesStatus } from "store/transferTermSalesStore";
import type { ITransferTerm } from "store/transferTermsStore";
import { downloadTransferTerms, fetchTransferTerms } from "store/transferTermsStore";

import AddTransfereeRightsModal from "./AddTransfereeRightsModal";
import AdminImportModal from "./AdminImportModal";
import styles from "./style.module.scss";
import TransferTermDrawer from "./TransferTermDrawer";
import TransferTermSalesTable from "./TransferTermSalesTable";
import HandleRightsModal from "./TransferTermSalesTable/HandleRightsModal";
import PaymentDateModal from "./TransferTermSalesTable/PaymentDateModal";
import SaveInvoiceModal from "./TransferTermSalesTable/SaveInvoiceModal";
import TransferTermsTable from "./TransferTermsTable";

/**
 * Status options for sales (formatted as needed for the StatusChip component)
 */
export const salesStatusRules = (): Record<ETransferTermSalesStatus, IStatusRule> => {
    return {
        [ETransferTermSalesStatus.PAYED]: {
            color: "green",
            text: t("TransferTerms.SalesStatus.Payed"),
        },
        [ETransferTermSalesStatus.INVOICE_TO_ISSUE]: {
            color: "blue",
            text: t("TransferTerms.SalesStatus.SendInvoice"),
        },
        [ETransferTermSalesStatus.LATE_STATEMENT]: {
            color: "yellow",
            text: t("TransferTerms.SalesStatus.WaitingStatement"),
        },
        [ETransferTermSalesStatus.WAITING_STATEMENT]: {
            color: "yellow",
            text: t("TransferTerms.SalesStatus.WaitingStatement"),
        },
        [ETransferTermSalesStatus.DUE_LATER]: {
            color: "grey",
            text: t("TransferTerms.SalesStatus.Future"),
        },
        [ETransferTermSalesStatus.DUE_NEXT]: {
            color: "yellow",
            text: t("TransferTerms.SalesStatus.Soon"),
        },
        [ETransferTermSalesStatus.WAITING_PAYMENT]: {
            color: "orange",
            text: t("TransferTerms.SalesStatus.WaitingPayment"),
        },
        [ETransferTermSalesStatus.LATE_PAYMENT]: {
            color: "red",
            text: t("TransferTerms.SalesStatus.LatePayment"),
        },
    };
};

enum ETransferTermsTabs {
    PAYMENTS = "0",
    TERMS = "1",
}

/**
 *  Container to the TransferTerms page. It includes two tabs: one with the sales table and another with the terms table.
 **/
const TransferTerms = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const nav = useNavigate();
    usePageTitle(t("TransferTerms.Terms"));
    const { isAdminCrealo } = useAuth();

    const location = useLocation();
    const notificationSaleId = location?.state?.transferTermSalesId;
    const [tabValue, setTabValue] = useState(location?.state?.tabValue ?? ETransferTermsTabs.PAYMENTS);
    const [queryChanged, setQueryChanged] = useState(false);
    const [adminImportOpen, setAdminImportOpen] = useState(false);

    // Used for the year selector:
    const [date, setDate] = useState<Date | null>(null);
    const minDate = new Date().setFullYear(2020);
    const debouncedDateValue = useDebounce(date, 500);

    const [currentTerm, setCurrentTerm] = useState<ITransferTerm>();
    const [currentSale, setCurrentSale] = useState<ITransferTermSale>();

    // Sales of term whose drawer has been opened.
    const [currentSales, setCurrentSales] = useState<ITransferTermSale[]>([]);
    const [formMode, setFormMode] = useState<string>("");
    const [modalType, setModalType] = useState<string>("");

    // TransferTermDrawer can be accessed from either the term table or the sales table.
    const [drawerOrigin, setDrawerOrigin] = useState<"sale" | "term">();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const { transferTerms, transferTermsCount, termsFetchStatus } = useAppSelector((state) => state.transferTerms);
    const {
        transferTermSales,
        salesCount,
        countStatus2,
        countStatus3,
        countStatus4,
        countStatus5,
        countStatus6,
        countStatus7,
    } = useAppSelector((state) => state.transferTermSales);

    /**
     * Payment linked to notification.
     */
    const paymentFromNotification = transferTermSales.find((item) => item.id === notificationSaleId);

    const { hasBexioIntegration } = useAppSelector((state) => state.integrations);

    /**
     * Options for the ThreeDotMenuButton on tabs line.
     * The admin import entry is filtered out entirely for non-admins so it never lands in the DOM.
     */
    const sectionTopActions: ThreeDotMenuOptions[] = [
        {
            id: "1",
            name: t("TransferTerms.DownloadFile", {
                data:
                    tabValue === ETransferTermsTabs.PAYMENTS
                        ? t("TransferTerms.Payments").toLowerCase()
                        : t("TransferTerms.Contracts").toLowerCase(),
            }),
            icon: <DownloadOutline width="16" height="16" />,
            action: () => handleDownloadData(),
        },
        ...(isAdminCrealo
            ? [
                  {
                      id: "admin-import",
                      name: t("TransferTerms.AdminImport.MenuLabel"),
                      icon: <Upload />,
                      action: () => setAdminImportOpen(true),
                  },
              ]
            : []),
    ];

    const {
        query,
        handleQuery,
        count,
        page,
        limitPagination,
        sortBy,
        sortOrder,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        handleRequest,
        transferStatus,
        setTransferStatus,
        setYear,
        lineType,
        setLineType,
    } = useAPIPagination({
        type: tabValue === ETransferTermsTabs.PAYMENTS ? "transferTermSales" : "transferTerms",
        sortBy: "status",
        sortOrder: "asc",
    });

    /**
     * Functions and data needed for the LastElementTable component.
     */
    const APIData = {
        count,
        page,
        limitPagination,
        sortBy,
        sortOrder,
        handleChangePage,
        handleChangeLimit,
        handleSort,
    };

    /**
     * Handles downloading Excel files with tables data.
     */
    const handleDownloadData = () => {
        if (tabValue === ETransferTermsTabs.PAYMENTS) {
            dispatch(downloadTransferTermSales());
        } else {
            dispatch(downloadTransferTerms());
        }
    };

    const handleTabChange = async (event: React.SyntheticEvent, newValue: string) => {
        // Needed to clear state or it kept saving "1" (from previous actions like creating new term) even if I hardRefreshed:
        window.history.replaceState({}, "");

        await setTabValue(newValue);
        await setTransferStatus("all");

        // The request is sent before tabValue changes (even with the await), so I need to force the fetch.
        if (newValue === ETransferTermsTabs.PAYMENTS && transferTermSales.length < 1) {
            handleRequest({ defaultType: "transferTermSales" });
        } else if (newValue === ETransferTermsTabs.TERMS && transferTerms.length < 1) {
            handleRequest({ defaultType: "transferTerms" });

            // Both fetches below are done if they have an active query and they switch tabs or a query existed and they delete it:
        } else if (newValue === ETransferTermsTabs.PAYMENTS && queryChanged) {
            if (query) {
                handleRequest({ defaultType: "transferTermSales", savedQuery: query });
                setQueryChanged(false);
            } else {
                handleRequest({ defaultType: "transferTermSales" });
                setQueryChanged(false);
            }
        } else if (newValue === ETransferTermsTabs.TERMS && queryChanged) {
            if (query) {
                handleRequest({ defaultType: "transferTerms", savedQuery: query });
                setQueryChanged(false);
            } else {
                handleRequest({ defaultType: "transferTerms" });
                setQueryChanged(false);
            }
        }
    };

    /**
     * Toggles TransferTermDrawer
     * @param value Whether we need to open or close it.
     * @param transferTerm The actual term (when accessed form term table) or the term id (when accessed from sale)
     */
    const handleToggleDrawer = async (
        value: boolean,
        transferTerm?: ITransferTerm | number,
        from?: "sale" | "term",
    ) => {
        let termToOpen;
        if (typeof transferTerm === "number") {
            termToOpen = transferTerms.find((term) => term.id === transferTerm);
        } else {
            termToOpen = transferTerm;
        }

        if (value && termToOpen) {
            await setDrawerOrigin(from);
            await setCurrentTerm(termToOpen);
            setDrawerOpen(true);
        } else {
            await setDrawerOrigin(undefined);
            await setCurrentTerm(undefined);
            setDrawerOpen(false);
        }
    };

    /**
     * Opens modal for sale actions.
     * @param event Action event.
     * @param currentSale Sale from which the action is triggered.
     * @param modalType Type of modal to open.
     * @param mode (new or edit) Needed if there are two states for the modal (mainly addRights)
     */
    const handleOpeningModal = async (
        currentSale: ITransferTermSale | undefined,
        modalType: string,
        mode?: string,
        event?: any,
    ) => {
        if (event) event.stopPropagation();
        await setCurrentSale(currentSale);
        if (mode) {
            await setFormMode(mode);
        }
        setModalType(modalType);
    };

    const handleCloseModal = ({
        updatedSale,
        updatedSales,
    }: {
        updatedSale?: ITransferTermSale;
        updatedSales?: ITransferTermSale[];
    }) => {
        setModalType("");
        updatedSale ? setCurrentSale(updatedSale) : setCurrentSale(undefined);
        updatedSales ? setCurrentSales(updatedSales) : setCurrentSales([]);
    };

    /**
     * Changes input value on Year selector + triggers fetch with year filter on useAPIPagination.
     * @param newValue
     */
    const handleChangeDate = (newValue: dateFns | Date) => {
        if (newValue) {
            setDate(newValue as Date);
            setYear((newValue as Date).getFullYear());
        } else {
            setDate(null);
            setYear(null);
        }
    };

    /**
     * Filters for the Sales ControlFilter.
     */
    const salesStatusFilterOptions = [
        { value: "all", label: t("Filters.AllMasculine") },
        { value: [2, 3], label: t("TransferTerms.SalesStatus.WaitingStatement"), count: countStatus2 + countStatus3 },
        { value: 4, label: t("TransferTerms.SalesStatus.SendInvoice"), count: countStatus4 },
        { value: 5, label: t("TransferTerms.SalesStatus.WaitingPayment"), count: countStatus5 },
        { value: 7, label: t("TransferTerms.SalesStatus.LatePayment"), count: countStatus7 },
        { value: 6, label: t("TransferTerms.SalesStatus.Payed"), count: countStatus6 },
    ];

    /**
     * Filters for the Terms ControlFilter.
     */
    const termStatusFilterOptions = [
        { value: "all", label: t("Filters.AllMasculine") },
        { value: 2, label: t("TransferTerms.TermsStatus.Negotiating") },
        { value: 0, label: t("TransferTerms.TermsStatus.ToSign") },
        { value: 3, label: t("TransferTerms.TermsStatus.Active") },
        { value: 1, label: t("TransferTerms.TermsStatus.EndsSoon") },
        { value: 4, label: t("TransferTerms.TermsStatus.Finished") },
    ];

    const salesTypeFilterOptions = [
        { value: null, label: t("Filters.AllMasculine") },
        { value: "sales", label: t("TransferTerms.Royalties") },
        { value: "advance", label: t("TransferTerms.Advance") },
    ];

    // In case we still don't have transferTerms, need to fetch them to open TransferTermDrawer from sales.
    useEffect(() => {
        const options = { limit: 100, sortBy: "id", sortOrder: "desc" };
        if (tabValue !== ETransferTermsTabs.TERMS && termsFetchStatus === "idle") {
            dispatch(fetchTransferTerms({ options }));
        }
    }, [tabValue, transferTerms]);

    useEffect(() => {
        if (termsFetchStatus !== "idle" && !transferTerms.length) {
            setTabValue("1");
        }
    }, [termsFetchStatus]);

    return (
        <AuthPage userAuthenticated pageName="transfer-terms">
            <div className={"transfers-container"}>
                <TabContext value={tabValue}>
                    <div className={styles["transfers-header"]}>
                        <h1>{t("TransferTerms.Terms")}</h1>
                        <ExpandedHelpdeskIcon
                            link="https://helpdesk.crealo.app/fr/category/module-de-cessions-de-droits-1m01d27/"
                            text={t("TransferTerms.Helpdesk.TransfersPage")}
                            style={{ paddingBottom: "0px" }}
                        />
                    </div>
                    <div className={styles["transfers-tabs-buttons"]}>
                        <StyledTabs
                            value={tabValue}
                            className={classNames({
                                [styles["transfers-page-tabs"]]: true,
                                [styles["payment-tab-active"]]: tabValue === ETransferTermsTabs.PAYMENTS,
                            })}
                            onChange={handleTabChange}
                        >
                            <Tooltip title={!transferTerms.length ? t("TransferTerms.PaymentTabDisabled") : ""} arrow>
                                {/** Tooltip only works around div. But doing so breaks the interactiveness of the Tab component. */}
                                <div
                                    onClick={(e) => handleTabChange(e, ETransferTermsTabs.PAYMENTS)}
                                    className={classNames({
                                        [styles["tab-active"]]: tabValue === ETransferTermsTabs.PAYMENTS,
                                    })}
                                >
                                    <Tab
                                        label={t("TransferTerms.Payments")}
                                        value={ETransferTermsTabs.PAYMENTS}
                                        disabled={!transferTerms.length}
                                    />
                                </div>
                            </Tooltip>
                            <Tab label={t("TransferTerms.TransferTerms")} value={ETransferTermsTabs.TERMS} />
                        </StyledTabs>
                        <div className={styles["transfers-page-buttons"]}>
                            <Tooltip
                                title={
                                    !transferTermSales.length && tabValue === ETransferTermsTabs.PAYMENTS
                                        ? t("TransferTerms.DisabledActionButton")
                                        : ""
                                }
                                arrow
                            >
                                <div>
                                    <StyledButton
                                        buttonType="primary"
                                        style={{ height: "1.7rem" }}
                                        disabled={!transferTermSales.length && tabValue === ETransferTermsTabs.PAYMENTS}
                                        onClick={() =>
                                            tabValue === ETransferTermsTabs.PAYMENTS
                                                ? setModalType("bulkRights")
                                                : nav("/transfer-terms/new")
                                        }
                                    >
                                        <Plus />
                                        {tabValue === ETransferTermsTabs.PAYMENTS
                                            ? t("TransferTerms.RightsPerTransferee")
                                            : t("TransferTerms.NewTerm")}
                                    </StyledButton>
                                </div>
                            </Tooltip>
                            <ThreeDotMenu options={sectionTopActions} />
                        </div>
                    </div>
                    <div className={"table-filters"}>
                        <SmallSearchBar
                            filter={query}
                            onSearch={(e) => {
                                handleQuery(e);
                                setQueryChanged(true);
                            }}
                        />
                        {tabValue === ETransferTermsTabs.PAYMENTS && (
                            <YearDatePicker
                                label={t("Payment.Year")}
                                date={debouncedDateValue}
                                minDate={minDate}
                                onChange={handleChangeDate}
                                renderInput={(params) => <StyledTextField fullWidth {...params} />}
                            />
                        )}
                        {tabValue === ETransferTermsTabs.PAYMENTS && (
                            <ControlFilter
                                filterLabel={t("TransferTerms.PaymentType")}
                                filterOptions={salesTypeFilterOptions}
                                controlValue={lineType}
                                onValueChange={(value) => setLineType(value)}
                            />
                        )}
                        <ControlFilter
                            filterLabel={
                                tabValue === ETransferTermsTabs.PAYMENTS
                                    ? t("TransferTerms.SalesStatus.SaleStatus")
                                    : t("TransferTerms.TermsStatus.TermStatus")
                            }
                            filterOptions={
                                tabValue === ETransferTermsTabs.PAYMENTS
                                    ? salesStatusFilterOptions
                                    : termStatusFilterOptions
                            }
                            controlValue={transferStatus}
                            onValueChange={(value) => {
                                setTransferStatus(value);
                            }}
                            selector={tabValue === ETransferTermsTabs.PAYMENTS}
                            btnWidth="165px"
                        />
                    </div>
                    <TabPanel
                        value={ETransferTermsTabs.PAYMENTS}
                        style={{ padding: "12px 0 0 0", maxWidth: "100%" } as React.CSSProperties}
                        className={"transfer-sales-table"} // need this class to be normal scss so I can access it from child components.
                    >
                        <TransferTermSalesTable
                            termSales={transferTermSales}
                            termSalesCount={salesCount}
                            handleToggleDrawer={handleToggleDrawer}
                            salesStatusRules={salesStatusRules()}
                            APIData={APIData}
                            modalType={modalType}
                            setModalType={setModalType}
                            setCurrentSale={setCurrentSale}
                            currentSale={currentSale}
                            handleOpeningModal={handleOpeningModal}
                            hasBexio={hasBexioIntegration}
                            transferTerms={transferTerms}
                            handleQuery={handleQuery}
                            notificationPayment={paymentFromNotification}
                        />
                        <ModalBase
                            open={modalType === "bulkRights"}
                            onClose={() => setModalType("")}
                            modalTitle={t("TransferTerms.RightsPerTransferee")}
                            modalSubtitle={t("TransferTerms.Modals.RightsTransfereeSubtitle")}
                            widthTransition={{ width: "800px", active: true }}
                            infoTooltip={{
                                text: t("TransferTerms.Helpdesk.AccessDoc"),
                                link: "https://helpdesk.crealo.app/fr/article/renseigner-des-droits-par-cessionnaire-z3xxr7/",
                            }}
                        >
                            <AddTransfereeRightsModal
                                transferTermSales={transferTermSales}
                                handleClose={() => setModalType("")}
                            />
                        </ModalBase>
                    </TabPanel>
                    <TabPanel
                        value={ETransferTermsTabs.TERMS}
                        style={{ padding: "12px 0px" } as React.CSSProperties}
                        className="transfer-terms-table"
                    >
                        <TransferTermsTable
                            transferTerms={transferTerms}
                            transferTermSales={transferTermSales}
                            termsFetchStatus={termsFetchStatus}
                            APIData={APIData}
                            termsCount={transferTermsCount}
                            handleToggleDrawer={handleToggleDrawer}
                            setCurrentTerm={setCurrentTerm}
                            currentTerm={currentTerm}
                            handleQuery={handleQuery}
                        />
                    </TabPanel>

                    {/** TRANSFER TERM DRAWER */}
                    <Drawer open={drawerOpen} onClose={() => handleToggleDrawer(false)} anchor="right">
                        <TransferTermDrawer
                            transferTerm={currentTerm}
                            handleToggleDrawer={handleToggleDrawer}
                            salesStatusRules={salesStatusRules()}
                            from={drawerOrigin}
                            handleOpeningModal={handleOpeningModal}
                            currentSales={currentSales}
                            setCurrentSales={setCurrentSales}
                        />
                    </Drawer>

                    {/** MODALS FOR SALES UPDATE -- those shared between payments table and term drawer */}
                    <ModalBase
                        modalTitle={
                            formMode === "new" ||
                            (currentSale && currentSale?.status < ETransferTermSalesStatus.INVOICE_TO_ISSUE)
                                ? t("TransferTerms.Modals.EnterRoyalties")
                                : t("TransferTerms.Modals.UpdateRoyalties")
                        }
                        modalSubtitle={t("TransferTerms.Modals.EnterRoyaltiesSubtitle")}
                        open={modalType === "addRights"}
                        onClose={() => setModalType("")}
                        infoTooltip={{
                            text: t("TransferTerms.Helpdesk.AccessDoc"),
                            link: "https://app.crisp.chat/website/6a9a2cd4-4735-4500-ad76-0bc81e596c87/helpdesk/articles/fr/8870dcd1-af26-43b5-987c-392627ef2770/",
                        }}
                    >
                        <HandleRightsModal
                            termSale={currentSale}
                            handleClose={handleCloseModal}
                            formMode={formMode}
                            currentSales={currentSales}
                        />
                    </ModalBase>
                    <ModalBase
                        modalTitle={
                            formMode === "new"
                                ? t("TransferTerms.Modals.SendInvoiceQuestion")
                                : t("TransferTerms.Modals.SaveInvoice")
                        }
                        modalSubtitle={t("TransferTerms.Modals.SaveInvoiceSubtitle")}
                        open={modalType === "addInvoice"}
                        onClose={() => setModalType("")}
                        infoTooltip={{
                            text: t("TransferTerms.Helpdesk.AccessDoc"),
                            link: "https://helpdesk.crealo.app/fr/article/enregistrer-une-facture-gxoep6/",
                        }}
                    >
                        <SaveInvoiceModal
                            termSale={currentSale}
                            handleClose={handleCloseModal}
                            formMode="new"
                            currentSales={currentSales}
                            hasBexio={hasBexioIntegration}
                        />
                    </ModalBase>
                    <ModalBase
                        modalTitle={
                            formMode === "new"
                                ? t("TransferTerms.Modals.PayQuestion")
                                : t("TransferTerms.Modals.UpdatePayment")
                        }
                        modalSubtitle={t("TransferTerms.Modals.PayModalSubtitle")}
                        open={modalType === "payStatus"}
                        onClose={() => setModalType("")}
                        infoTooltip={
                            formMode === "edit"
                                ? {
                                      text: t("TransferTerms.Helpdesk.AccessDoc"),
                                      link: "https://helpdesk.crealo.app/fr/article/cessions-comment-changer-la-date-de-perception-dun-paiement-1wyhd01/",
                                  }
                                : undefined
                        }
                    >
                        <PaymentDateModal
                            termSale={currentSale}
                            handleClose={handleCloseModal}
                            formMode={formMode}
                            currentSales={currentSales}
                        />
                    </ModalBase>
                    {isAdminCrealo && (
                        <AdminImportModal
                            open={adminImportOpen}
                            onClose={() => setAdminImportOpen(false)}
                            onImported={() =>
                                dispatch(
                                    fetchTransferTerms({ options: { limit: 100, sortBy: "id", sortOrder: "desc" } }),
                                )
                            }
                        />
                    )}
                </TabContext>
            </div>
        </AuthPage>
    );
};

export default TransferTerms;
